import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../stores/Stores";
import { Button } from "@fluentui/react-components";
import {
    PeopleRegular,
    EditRegular,
    BuildingBankRegular,
    AddRegular,
    ChatRegular,
    CalendarRegular,
} from "@fluentui/react-icons";
import DateField from "../../form/dateField/DateField";
import TextField from "../../form/textField/TextField";
import CheckBox from "../../form/checkbox/CheckBox";
import styles from "../customerMeetings.module.scss";
import { BusinessLine } from "../../shared/types/BusinessLine";
import {
    validateC6,
    mapStringToArray,
    validateAttendeeEmails,
    mapArrayToString,
    validateTitle,
    validateMeetingEndingDate,
} from "../MeetingHelpers";
import { isRegonValid } from "../../shared/utils/validation";
import { FormActionType } from "./FormActionType";
import { CustomerMeeting } from "../store/CustomerMeeting";
import { useTranslation } from "../../shared/translation/Translation";
import { LanguageVersion } from "../../shared/types/LanguageVersion";
import AntiXssTextField from "../../form/textField/AntiXssTextField";
import { getMeetingDefaultEndTime, getMeetingMinTime } from "../../shared/utils/dateUtils";
import EmployeesField from "../../form/employeesField/EmployeesField";
import { clearSeconds } from "../../shared/utils/dateUtils";
import { Customer, ICustomer } from "../../models/Customer";
import CustomersField, { CustomersFieldRef } from "../../form/customerField/CustomersField";
import { MeetingKind } from "../../shared/types/MeetingKind";
import Section from "../../form/section/Section";
import EmployeesAddAndEditField from "../../form/employeesAddAndEditField/EmployeesAddAndEditField";

interface CorporateMeetingFormProps {
    formActionType: FormActionType;
    businessLine: BusinessLine;
    meeting?: CustomerMeeting;

    onClose: () => void;
    onSuccess?: () => void;
}

const CorporateMeetingForm: React.FC<CorporateMeetingFormProps> = observer((props: CorporateMeetingFormProps) => {
    const { meetingStore, userStore } = useStore();
    const messages = useTranslation();

    const [titleText, setTitleText] = useState<string>("");
    const [companyNameText, setCompanyNameText] = useState<string>("");
    const [c6NumberText, setC6NumberText] = useState<string>("");
    const [regonNumberText, setRegonNumberText] = useState<string>("");
    const [additionalAttendeesEmailsText, setAdditionalAttendeesEmailsText] = useState<string>("");
    const [startingDate, setStartingDate] = useState(new Date());
    const [endingDate, setEndingDate] = useState(new Date());
    const [isEnglishCommunicationChecked, setIsEnglishCommunicationChecked] = useState<boolean>(false);
    const [isVirtualAppointment, setIsVirtualAppointment] = useState<boolean>(false);

    const [contactPeople, setContactPeople] = useState<ICustomer[]>([]);
    const [newAdditionalAttendeesEmailsText, setNewAdditionalAttendeesEmailsText] = useState<string>("");
    const [
        isAdditionalAttendeesFieldRequestedExpanded,
        setIsAdditionalAttendeesFieldRequestedExpanded,
    ] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(true);

    const isFormTypeUpdate = props.formActionType === FormActionType.UpdateMeeting;
    const isCorporateMeeting = true;

    const customersRef = useRef<CustomersFieldRef>(null);

    useEffect(() => {
        if (isFormTypeUpdate) {
            setTitleText(props.meeting!.subject);
            setCompanyNameText(props.meeting!.companyName);
            setContactPeople(props.meeting!.customers);
            setStartingDate(props.meeting!.startAt);
            setEndingDate(props.meeting!.endAt);
            setIsEnglishCommunicationChecked(props.meeting!.languageVersion === LanguageVersion.English);
            setIsVirtualAppointment(props.meeting?.meetingKind === MeetingKind.VirtualAppointment);

            if (props.meeting!.c6) {
                setC6NumberText(props.meeting!.c6!);
            }

            if (props.meeting!.regon) {
                setRegonNumberText(props.meeting!.regon!);
            }

            if (props.meeting!.additionalAttendeesEmails) {
                setAdditionalAttendeesEmailsText(mapArrayToString(props.meeting!.additionalAttendeesEmails!));
            }
        } else {
            const defaultStartingDate = getMeetingMinTime(new Date());
            setStartingDate(defaultStartingDate);
            setEndingDate(getMeetingDefaultEndTime(defaultStartingDate));
            setIsVirtualAppointment(
                meetingStore.meetingOptions.defaultMeetingKind === MeetingKind.VirtualAppointment ? true : false
            );
            setContactPeople([Customer.New()]);
        }
        setIsLoading(false);
    }, []);

    const validateAllFields = () => {
        if (isFormTypeUpdate) {
            return (
                validateAttendeeEmails(newAdditionalAttendeesEmailsText) &&
                validateMeetingEndingDate(endingDate, maxDate) &&
                contactPeople
                    .filter((person) => person.isNew)
                    .every((person) => person.validateCustomer(isCorporateMeeting).isValid)
            );
        }

        return (
            titleText.length > 0 &&
            validateTitle(titleText) &&
            validateCompanyIdentificationFields() &&
            validateAttendeeEmails(additionalAttendeesEmailsText) &&
            validateMeetingEndingDate(endingDate, maxDate) &&
            contactPeople.length > 0 &&
            contactPeople.every((person) => person.validateCustomer(isCorporateMeeting).isValid)
        );
    };

    const validateCompanyIdentificationFields = () => {
        if (c6NumberText.length && regonNumberText.length) {
            return validateC6(c6NumberText) && isRegonValid(regonNumberText);
        }

        return c6NumberText.length ? validateC6(c6NumberText) : isRegonValid(regonNumberText);
    };

    const createMeeting = async () => {
        await meetingStore.createMeeting({
            subject: titleText,
            customers: contactPeople,
            startAt: clearSeconds(startingDate),
            endAt: clearSeconds(endingDate),
            additionalAttendeesEmails: mapStringToArray(additionalAttendeesEmailsText),
            businessLine: props.businessLine,
            companyName: companyNameText,
            c6: c6NumberText,
            regon: regonNumberText,
            languageVersion: isEnglishCommunicationChecked ? LanguageVersion.English : userStore.languageVersion,
            businessLineLanguageVersion: userStore.languageVersion,
            meetingKind: isVirtualAppointment ? MeetingKind.VirtualAppointment : MeetingKind.MsTeams,
        });

        if (!meetingStore.createMeetingRequestStatus.isPending) {
            props.onClose();
        }
    };

    const updateMeeting = async () => {
        await meetingStore.updateMeeting({
            urlHash: props.meeting!.urlHash,
            customers: contactPeople.filter((person) => person.isNew),
            startAt: clearSeconds(startingDate),
            endAt: clearSeconds(endingDate),
            additionalAttendeesEmails: mapStringToArray(newAdditionalAttendeesEmailsText),
        });

        if (meetingStore.updateMeetingRequestStatus.isDone) {
            props.onSuccess!();
        } else if (!meetingStore.updateMeetingRequestStatus.isPending) {
            props.onClose();
        }
    };

    const isShowAdditionalAttendees = (): boolean => {
        return (
            isAdditionalAttendeesFieldRequestedExpanded ||
            additionalAttendeesEmailsText.length > 0 ||
            newAdditionalAttendeesEmailsText.length > 0
        );
    };

    if (isLoading) {
        return null;
    }

    const minDate = new Date();
    const maxDate = new Date(minDate);
    maxDate.setDate(maxDate.getDate() + meetingStore.meetingOptions.maxNumberOfDaysToPlanMeeting);

    return (
        <>
            <TextField
                text={titleText}
                handleDataChange={setTitleText}
                required={true}
                placeholder={messages.meetingModals.titlePlaceholder}
                iconComponent={<EditRegular />}
                showSuccessIndicator={!!titleText.length && validateTitle(titleText)}
                isInvalid={validateTitle(titleText) === false}
                errorMessage={messages.meetingModals.validation.invalidTitle}
                disabled={isFormTypeUpdate}
            />
            <AntiXssTextField
                text={companyNameText}
                handleDataChange={setCompanyNameText}
                required={true}
                placeholder={messages.meetingModals.corporate.companyNamePlaceholder}
                iconComponent={<PeopleRegular />}
                disabled={isFormTypeUpdate}
            />
            <TextField
                text={c6NumberText}
                handleDataChange={setC6NumberText}
                required={true}
                placeholder={messages.meetingModals.corporate.c6NumberPlaceholder}
                iconComponent={<BuildingBankRegular />}
                showSuccessIndicator={!!c6NumberText.length && validateC6(c6NumberText)}
                isInvalid={!validateC6(c6NumberText)}
                errorMessage={messages.meetingModals.validation.invalidC6}
                disabled={isFormTypeUpdate}
            />
            <TextField
                text={regonNumberText}
                handleDataChange={setRegonNumberText}
                required={true}
                placeholder={messages.meetingModals.corporate.regonPlaceholder}
                iconComponent={<BuildingBankRegular />}
                isInvalid={!!regonNumberText.length && !isRegonValid(regonNumberText)}
                showSuccessIndicator={!!regonNumberText.length && isRegonValid(regonNumberText)}
                errorMessage={messages.meetingModals.validation.invalidRegon}
                disabled={isFormTypeUpdate}
            />
            <Section
                headerMessage={messages.meetingModals.customers}
                body={
                    <CustomersField
                        ref={customersRef}
                        customers={contactPeople}
                        handleDataChange={setContactPeople}
                        isFormTypeUpdate={isFormTypeUpdate}
                        isCorporateMeeting={isCorporateMeeting}
                    />
                }
                headerNode={
                    <Button
                        icon={<AddRegular />}
                        appearance="transparent"
                        onClick={() => {
                            customersRef.current?.addNew();
                        }}
                    >
                        {isCorporateMeeting
                            ? messages.meetingModals.corporate.addContactPerson
                            : messages.meetingModals.addClient}
                    </Button>
                }
            />

            <Section
                headerMessage={messages.meetingModals.consultants}
                tooltipMessage={messages.meetingModals.consultantsToolip}
                body={
                    <EmployeesAddAndEditField
                        isFormTypeUpdate={isFormTypeUpdate}
                        additionalAttendeesEmailsText={additionalAttendeesEmailsText
                            .split(";")
                            .filter((item) => item.trim() !== "")}
                        newAdditionalAttendeesEmailsText={newAdditionalAttendeesEmailsText
                            .split(";")
                            .filter((item) => item.trim() !== "")}
                        onAdditionalAttendeesEmailsDataChange={(emails) => {
                            setAdditionalAttendeesEmailsText(emails.join(";"));
                        }}
                        onNewAdditionalAttendeesEmailsDataChange={(emails) => {
                            setNewAdditionalAttendeesEmailsText(emails.join(";"));
                        }}
                        isExpanded={isShowAdditionalAttendees()}
                    />
                }
                headerNode={
                    isShowAdditionalAttendees() === false && (
                        <Button
                            icon={<AddRegular />}
                            appearance="transparent"
                            onClick={() => {
                                setIsAdditionalAttendeesFieldRequestedExpanded(true);
                            }}
                        >
                            {messages.meetingModals.add}
                        </Button>
                    )
                }
            />

            <CheckBox
                label={messages.meetingModals.englishCommunicationCheckboxLabel}
                toggle
                isChecked={isEnglishCommunicationChecked}
                iconComponent={<ChatRegular />}
                handleDataChange={setIsEnglishCommunicationChecked}
                disabled={isFormTypeUpdate}
            />
            <DateField
                startingDate={startingDate}
                setStartingDate={setStartingDate}
                endingDate={endingDate}
                setEndingDate={setEndingDate}
                minDate={minDate}
                maxDate={maxDate}
                isInvalid={!validateMeetingEndingDate(endingDate, maxDate)}
                errorMessage={messages.meetingModals.validation.invalidMeetingEndingDate(
                    meetingStore.meetingOptions.maxNumberOfDaysToPlanMeeting
                )}
            />
            {meetingStore.meetingOptions.showMeetingKindToggle ? (
                <CheckBox
                    label={messages.meetingModals.useVirtualAppointments}
                    toggle
                    iconComponent={<CalendarRegular />}
                    isChecked={isVirtualAppointment}
                    handleDataChange={setIsVirtualAppointment}
                    disabled={isFormTypeUpdate}
                />
            ) : (
                <></>
            )}
            <div className={styles.buttonRow}>
                <Button
                    disabled={
                        meetingStore.createMeetingRequestStatus.isPending ||
                        meetingStore.updateMeetingRequestStatus.isPending ||
                        !validateAllFields()
                    }
                    appearance="primary"
                    onClick={isFormTypeUpdate ? updateMeeting : createMeeting}
                >
                    {isFormTypeUpdate ? messages.updateMeeting.updateButton : messages.createMeeting.createButton}
                </Button>
                <Button onClick={props.onClose}>{messages.meetingModals.closeButton}</Button>
            </div>
        </>
    );
});

export default CorporateMeetingForm;
